import { useState, useEffect } from 'react';
import {
  onSnapshot,
  doc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Game } from '@/lib/types';

import * as PlayService from '@/services/game-play.service';

const SNAPSHOT_FALLBACK_MS = 3000;

interface UseGameReturn {
  game: Game | null;
  loading: boolean;
}

export function useGame(gameId?: string): UseGameReturn {
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) {
      setGame(null);
      setLoading(false);
      return;
    }
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setGame(null);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    const unsub = onSnapshot(
      doc(db, 'games', gameId),
      (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        if (snap.exists()) {
          setGame({ id: snap.id, ...snap.data() } as Game);
        } else {
          setGame(null);
        }
        setLoading(false);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useGame error:', error);
        setGame(null);
        setLoading(false);
      }
    );
    return () => {
      settled = true;
      window.clearTimeout(timeout);
      unsub();
    };
  }, [gameId]);

  return { game, loading };
}

export async function endGame(gameId: string): Promise<void> {
  return PlayService.endGame(gameId);
}

export async function addBuyIn(
  gameId: string,
  playerUserId: string,
  amount: number,
  type: 'digital' | 'cash' = 'digital',
  actorUserId: string,
  isCorrection: boolean = false
): Promise<void> {
  return PlayService.addBuyIn(gameId, playerUserId, amount, type, actorUserId, isCorrection);
}

export async function batchUpdateChipCounts(
  gameId: string,
  stacks: Record<string, number>,
  adminUserId: string
): Promise<void> {
  return PlayService.batchUpdateChipCounts(gameId, stacks, adminUserId);
}

export async function markPlayerLeftEarly(
  gameId: string,
  adminUserId: string,
  playerUserId: string,
  stack: number,
  cash: number
): Promise<void> {
  return PlayService.markPlayerLeftEarly(gameId, adminUserId, playerUserId, stack, cash);
}

export async function rejoinPlayer(
  gameId: string,
  adminUserId: string,
  playerUserId: string
): Promise<void> {
  return PlayService.rejoinPlayer(gameId, adminUserId, playerUserId);
}
